import imageCompression from 'browser-image-compression';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { BilingualField } from '../components/BilingualField';
import {
  bilingualPayload,
  emptyBilingual,
  hasBilingualValue,
  type BilingualValue,
} from '../components/bilingualValue';
import { useAuth } from '../hooks/useAuth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function ApplyHost() {
  const { user, loading: authLoading, getToken } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState<BilingualValue>(emptyBilingual());
  const [, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pendingApplication, setPendingApplication] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true, state: { from: '/apply-host' } });
      return;
    }
    if (user.isHost) {
      navigate('/host/activities', { replace: true });
      return;
    }
    if (user.isAdmin) {
      navigate('/admin/activities', { replace: true });
      return;
    }

    // Check if user already has a pending host application
    const checkHostStatus = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/host/apply-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.data?.status === 'PENDING') {
            setPendingApplication(true);
          } else if (data.data?.status === 'APPROVED') {
            navigate('/host/activities', { replace: true });
            return;
          }
        }
        // 404 means no host record — user can apply
      } catch {
        // Network error — allow form to show, backend will catch duplicates
      } finally {
        setCheckingStatus(false);
      }
    };
    checkHostStatus();
  }, [authLoading, user, navigate, getToken]);

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload an image file.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Image must be under 10MB.');
      return;
    }

    // Clear any previously uploaded avatar BEFORE the async compression/upload work.
    // Otherwise the submit button stays enabled with the old avatarUrl while a new
    // file is being processed, and the user could submit with the wrong image.
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatarUrl(null);
    setUploading(true);

    try {
      // Client-side compression: scale to long edge 512px, target ~100KB, force JPEG.
      // See docs/prd-avatar-compression.md §5.1 — we trust library output and do not
      // post-check size (100–200KB edge cases are acceptable).
      let renamedFile: File;
      try {
        const compressed = await imageCompression(file, {
          maxSizeMB: 0.1,
          maxWidthOrHeight: 512,
          useWebWorker: true,
          fileType: 'image/jpeg',
        });
        const stem = file.name.replace(/\.[^.]+$/, '');
        renamedFile = new File([compressed], `${stem}.jpg`, { type: 'image/jpeg' });
      } catch {
        setError('Please upload an image file.');
        return;
      }

      setAvatarFile(renamedFile);
      setAvatarPreview(URL.createObjectURL(renamedFile));

      const token = await getToken();

      const presignRes = await fetch(`${API_URL}/upload/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: renamedFile.name,
          fileType: 'image/jpeg',
          folder: 'hosts',
        }),
      });

      if (!presignRes.ok) {
        throw new Error('Failed to get upload URL.');
      }

      const presignData = await presignRes.json();
      const { uploadUrl, fileUrl } = presignData.data;

      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: renamedFile,
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload image.');
      }

      setAvatarUrl(fileUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
      setAvatarFile(null);
      setAvatarPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasBilingualValue(bio)) {
      setError('Enter a bio in Chinese or English.');
      return;
    }
    if (!avatarUrl) {
      setError('Please upload an avatar image.');
      return;
    }

    try {
      setSubmitting(true);
      const token = await getToken();

      const res = await fetch(`${API_URL}/host/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          displayName,
          firstName,
          lastName,
          avatarUrl,
          ...bilingualPayload(bio, 'bio'),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const msg =
          typeof data?.error === 'string'
            ? data.error
            : data?.error?.message || data?.message || 'Failed to submit application.';
        throw new Error(msg);
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || checkingStatus) return null;

  if (pendingApplication) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-stone-800 mb-2">Application Under Review</h2>
          <p className="text-stone-600 mb-6">
            Your host application has been submitted and is currently under review. We'll notify you
            once it's approved.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-stone-800 mb-2">Application Submitted!</h2>
          <p className="text-stone-600 mb-6">
            Your application has been submitted! We'll review it and get back to you soon.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center text-stone-500 hover:text-stone-700 transition-colors mb-6"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
          <h1 className="text-2xl font-bold text-stone-800 mb-2">Apply to Become a Host</h1>
          <p className="text-stone-500 mb-8">
            Share your passion by hosting activities for families. Fill out the form below to get
            started.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Legal Name (First + Last) */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Legal Name <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-stone-500 mb-1.5">
                For payouts and contracts — not shown publicly.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <input
                  id="firstName"
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors"
                  placeholder="First name"
                  aria-label="First name"
                />
                <input
                  id="lastName"
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors"
                  placeholder="Last name"
                  aria-label="Last name"
                />
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label
                htmlFor="displayName"
                className="block text-sm font-medium text-stone-700 mb-1"
              >
                Display Name <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-stone-500 mb-1.5">This is the name families will see.</p>
              <input
                id="displayName"
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors"
                placeholder="e.g. Sunny 老师"
              />
            </div>

            {/* Avatar Upload */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Avatar <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-4">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-stone-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-stone-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                )}
                <div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-4 py-2 text-sm bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : avatarUrl ? 'Change Image' : 'Choose Image'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarSelect}
                    className="hidden"
                  />
                  <p className="text-xs text-stone-400 mt-1">Photo, up to 10MB.</p>
                  {avatarUrl && (
                    <p className="text-xs text-green-600 mt-1">Uploaded successfully</p>
                  )}
                </div>
              </div>
            </div>

            {/* Bio: at least one language; the other is translated for review */}
            <BilingualField
              fieldType="hostBio"
              label="Bio"
              required
              multiline
              rows={4}
              stacked
              value={bio}
              onChange={setBio}
              translateOnMount={false}
              sideLabels={{ ZH: '个人简介 (中文)', EN: 'Personal Bio (English)' }}
              placeholder={{
                ZH: '家长会在活动页看到这段介绍，建议写 50–150 字介绍你的背景、经验和教学风格。',
                EN: 'Families will see this intro on your activity pages. About 100–300 characters covering your background, experience, and teaching style.',
              }}
              labelClassName="block text-sm font-medium text-stone-700 mb-1"
              inputClassName="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-colors resize-none"
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || uploading || !avatarUrl}
              className="w-full py-3 bg-amber-700 text-white font-medium rounded-lg hover:bg-amber-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
